import React, { useState } from 'react';
import { CheckCircle2, Loader2, AlertCircle, CalendarClock } from 'lucide-react';
import { joinWaitlist, type WaitlistAgeGroup, type WaitlistPeriod } from '../services/bookingApi';
import { describeError } from '../utils/describeError';

const fmt = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : null);

/**
 * Bloco #64: formulário de Lista de Espera Prioritária exibido no link externo
 * enquanto o Modo Recesso / Férias estiver ativo.
 */
export const WaitlistForm: React.FC<{
  slug: string;
  clinicName: string;
  recess: { returnDate: string | null; notice: string | null };
}> = ({ slug, clinicName, recess }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [period, setPeriod] = useState<WaitlistPeriod | ''>('');
  const [ageGroup, setAgeGroup] = useState<WaitlistAgeGroup | ''>('');
  const [lgpd, setLgpd] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ whatsapp: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) return setError('Informe seu nome completo.');
    if (phone.replace(/\D/g, '').length < 10) return setError('Informe um WhatsApp com DDD.');
    if (reason.trim().length < 5) return setError('Conte brevemente o motivo da procura.');
    if (!ageGroup) return setError('Selecione a faixa etária.');
    if (!lgpd) return setError('É necessário aceitar o uso dos dados para o contato de retorno.');
    setSending(true);
    try {
      const r = await joinWaitlist(slug, { name: name.trim(), phone: phone.trim(), reason: reason.trim(), preferredPeriod: period || undefined, ageGroup, lgpdConsent: true });
      setDone({ whatsapp: r.whatsapp });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSending(false);
    }
  };

  const input = 'w-full p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-semibold dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/10';
  const label = 'text-[10px] font-black uppercase tracking-widest text-slate-400';

  return (
    <section className="space-y-6">
      <div className="p-6 rounded-[2rem] bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 space-y-2">
        <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
          <CalendarClock size={20} className="text-amber-600" /> Estamos em recesso
        </h2>
        <p className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
          {recess.notice || `As atividades de ${clinicName} estão temporariamente em recesso.`}
          {fmt(recess.returnDate) && <> Retornamos em <strong>{fmt(recess.returnDate)}</strong>.</>}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
          Deixe seus dados na lista de espera prioritária e entraremos em contato assim que as triagens forem retomadas.
        </p>
      </div>

      {done ? (
        <div className="p-6 rounded-[2rem] bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 space-y-3">
          <p className="text-sm font-black text-emerald-800 dark:text-emerald-200 flex items-center gap-2"><CheckCircle2 size={18} /> Inscrição recebida!</p>
          <p className="text-xs text-emerald-900 dark:text-emerald-200 font-medium leading-relaxed">
            Seu contato está na nossa lista de espera prioritária.{' '}
            {done.whatsapp === 'ENVIADO' ? 'Enviamos uma confirmação no seu WhatsApp.' : 'Guarde este aviso: entraremos em contato pelo WhatsApp informado.'}
          </p>
          <p className="text-[11px] text-emerald-900/80 dark:text-emerald-300 font-medium leading-relaxed">
            Em caso de emergência ou crise, procure o serviço de saúde mais próximo (CAPS ou UPA) ou ligue <strong>188 (CVV)</strong>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Lista de espera prioritária</h3>
          {error && (
            <div className="flex gap-3 p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-xs font-bold">
              <AlertCircle size={18} className="shrink-0" /> {error}
            </div>
          )}
          <div className="space-y-1.5">
            <label className={label} htmlFor="wl-name">Nome completo *</label>
            <input id="wl-name" value={name} onChange={(e) => setName(e.target.value)} required className={input} placeholder="Seu nome completo" />
          </div>
          <div className="space-y-1.5">
            <label className={label} htmlFor="wl-phone">WhatsApp (com DDD) *</label>
            <input id="wl-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required inputMode="tel" className={input} placeholder="(00) 90000-0000" />
          </div>
          <div className="space-y-1.5">
            <label className={label} htmlFor="wl-reason">Motivo da procura *</label>
            <textarea id="wl-reason" value={reason} onChange={(e) => setReason(e.target.value)} required rows={3} className={`${input} resize-none`} placeholder="Conte brevemente o que motiva a busca por atendimento" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className={label} htmlFor="wl-period">Período de preferência</label>
              <select id="wl-period" value={period} onChange={(e) => setPeriod(e.target.value as WaitlistPeriod | '')} className={input}>
                <option value="">Sem preferência</option>
                <option value="MANHA">Manhã</option>
                <option value="TARDE">Tarde</option>
                <option value="NOITE">Noite</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className={label} htmlFor="wl-age">Faixa etária / público *</label>
              <select id="wl-age" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value as WaitlistAgeGroup | '')} required className={input}>
                <option value="">Selecione...</option>
                <option value="CRIANCA">Criança</option>
                <option value="ADOLESCENTE">Adolescente</option>
                <option value="ADULTO">Adulto</option>
                <option value="IDOSO">Idoso</option>
                <option value="FAMILIAR">Familiar</option>
                <option value="CASAL">Casal</option>
              </select>
            </div>
          </div>
          <label className="flex items-start gap-3 text-[11px] text-slate-600 dark:text-slate-400 font-medium leading-relaxed cursor-pointer">
            <input type="checkbox" checked={lgpd} onChange={(e) => setLgpd(e.target.checked)} required className="mt-0.5 rounded" />
            <span>Autorizo o uso dos meus dados exclusivamente para o contato de retorno da lista de espera (LGPD). *</span>
          </label>
          <button type="submit" disabled={sending} className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 size={16} className="animate-spin" /> : 'Entrar na lista de espera'}
          </button>
        </form>
      )}
    </section>
  );
};

export default WaitlistForm;
